import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import DashboardLayout from "../../../layouts/DashboardLayout";
import Loading from "../../../../../components/Loading/Loading";
import localeText from "../../../../../locale";
import { formatPrice } from "../../../../../utilities/formatPrice";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import ExtraPayService from "../../../../../service/ExtraPayService";
import WalletService from "../../../../../service/WalletService";
import SecurityValidationModal from "../components/SecurityValidationModal/SecurityValidationModal";
import {
  EXTRAPAY_OTP_INVALID_CODE,
  EXTRAPAY_PATH,
  EXTRAPAY_SUCCESS_CODE,
  EXTRAPAY_WITHDRAW_OTP_LENGTH,
  EXTRAPAY_WITHDRAW_OTP_TTL_MS,
  extrapayTabPath,
  extrapayWithdrawDetailsPath,
} from "./constants";
import { parseExtrapayAmount } from "./formatExtrapay";
import useExtrapayOverview from "./hooks/useExtrapayOverview";
import ExtrapayBreadcrumb from "./components/ExtrapayBreadcrumb";
import ExtrapayPageHeader from "./components/ExtrapayPageHeader";
import ExtrapayInfoNote from "./components/ExtrapayInfoNote";
import { WithdrawConfirmDialog, WithdrawSuccessDialog } from "./components/WithdrawDialogs";

const ExtrapayWithdrawPage = ({ userInfo }) => {
  const history = useHistory();
  const { showErrorToast } = useToastContext();
  const overview = useExtrapayOverview();
  const [address, setAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [errors, setErrors] = useState({});
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [otpVisible, setOtpVisible] = useState(false);
  const [created, setCreated] = useState(null);
  const [balanceAfter, setBalanceAfter] = useState(0);

  const parsedAmount = parseExtrapayAmount(amount);

  const validate = () => {
    const nextErrors = {};
    if (!address.trim()) nextErrors.address = localeText("extrapayFlowAddressRequired");
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      nextErrors.amount = localeText("extrapayFlowAmountRequired");
    } else if (parsedAmount > overview.excBalance) {
      nextErrors.amount = localeText("extrapayFlowAmountAboveBalance");
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const openConfirm = () => {
    if (!validate()) return;
    setChecked(false);
    setConfirmVisible(true);
  };

  const sendWithdrawCode = async () => {
    try {
      const res = await new WalletService().getWithdrawOtp(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
      );
      const ok = res?.messageCode === 200000 || res?.messageCode === 200001;
      if (!ok) showErrorToast({ code: res?.messageCode });
      return ok;
    } catch (error) {
      showErrorToast({ code: error?.response?.data?.messageCode || 500000 });
      return false;
    }
  };

  // Ao confirmar a revisão, envia o OTP por e-mail e abre a validação do código
  const requestCode = async () => {
    setSendingCode(true);
    const ok = await sendWithdrawCode();
    setSendingCode(false);
    if (ok) {
      setConfirmVisible(false);
      setOtpVisible(true);
    }
  };

  const resendCode = async () => {
    setResendingCode(true);
    const ok = await sendWithdrawCode();
    setResendingCode(false);
    return ok;
  };

  const confirmWithdraw = async (otp) => {
    setSubmitting(true);
    try {
      const service = new ExtraPayService(localStorage.getItem("__extraconomy_token"));
      const response = await service.requestWithdrawal({
        addressEXC: address.trim(),
        amount: parsedAmount,
        user: { username: localStorage.getItem("__extraconomy_username") },
        otp: (otp ?? "").trim(),
      });
      if (response?.messageCode === EXTRAPAY_OTP_INVALID_CODE) {
        showErrorToast({ detail: "extrapayFlowOtpInvalid" });
        return;
      }
      if (response?.messageCode !== EXTRAPAY_SUCCESS_CODE) {
        showErrorToast({ code: response?.messageCode });
        return;
      }
      const refreshed = await overview.reload();
      setCreated(response.result);
      setBalanceAfter(
        refreshed?.ok ? refreshed.excBalance : Math.max(overview.excBalance - parsedAmount, 0),
      );
      setOtpVisible(false);
    } catch (error) {
      showErrorToast({ code: error?.response?.data?.messageCode || 500000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <ExtrapayBreadcrumb
        items={[
          { labelKey: "financial", to: "/dashboard-financeiro" },
          { labelKey: "extrapayFlowTitle", to: EXTRAPAY_PATH },
          { labelKey: "extrapayFlowWithdrawTitle" },
        ]}
      />
      <ExtrapayPageHeader
        title={localeText("extrapayFlowWithdrawTitle")}
        subtitle={localeText("extrapayFlowWithdrawSubtitle")}
      />

      {overview.isLoading ? (
        <div className="flex justify-content-center py-6">
          <Loading classNameIcon="text-6xl" />
        </div>
      ) : (
        <>
          <div className="grid">
            <div className="col-12 lg:col-7">
              <div
                className="border-round-lg p-4"
                style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
              >
                <label className="text-sm font-medium" htmlFor="extrapay-address">
                  {localeText("extrapayFlowWalletAddress")}*
                </label>
                <InputText
                  id="extrapay-address"
                  className="w-full mt-2 border-round-md"
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                />
                <p className="text-sm mt-2 mb-0" style={{ color: "var(--theme-text-muted)" }}>
                  {localeText("extrapayFlowWalletAddressHint")}
                </p>
                {errors.address && (
                  <p className="text-sm mt-2 mb-0 text-red-500">{errors.address}</p>
                )}

                <label className="text-sm font-medium block mt-4" htmlFor="extrapay-amount">
                  {localeText("extrapayFlowWithdrawAmount")}
                </label>
                <InputText
                  id="extrapay-amount"
                  className="w-full mt-2 border-round-md"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
                <p className="text-sm mt-2 mb-0" style={{ color: "var(--theme-text-muted)" }}>
                  {localeText("extrapayFlowAvailableBalance", {
                    amount: formatPrice(overview.excBalance),
                  })}
                </p>
                {errors.amount && <p className="text-sm mt-2 mb-0 text-red-500">{errors.amount}</p>}

                <div className="flex align-items-center gap-3 mt-4">
                  <Button
                    label={localeText("extrapayFlowReviewWithdraw")}
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    className="border-round-md border-none text-sm font-medium p-3"
                    style={{ backgroundColor: "var(--theme-primary, #1DAF61)", color: "#fff" }}
                    onClick={openConfirm}
                    disabled={overview.hasError || overview.excBalance <= 0}
                  />
                  <button
                    type="button"
                    className="p-0 border-none bg-transparent cursor-pointer underline"
                    style={{ color: "var(--theme-secondary, #4B6087)" }}
                    onClick={() => history.push(EXTRAPAY_PATH)}
                  >
                    {localeText("cancel")}
                  </button>
                </div>
              </div>
            </div>
            <div className="col-12 lg:col-5">
              <div
                className="border-round-lg p-4 h-full"
                style={{ backgroundColor: "#F7F8FA", color: "var(--theme-text-muted, #5C5C5C)" }}
              >
                <div className="flex flex-column gap-3 mb-4">
                  <i
                    className="pi pi-info-circle text-lg"
                    style={{ color: "var(--theme-primary)" }}
                  />
                  <span
                    className="text-2xl font-medium"
                    style={{ color: "var(--theme-text, #172b4d)" }}
                  >
                    {localeText("extrapayFlowHowYouReceive")}
                  </span>
                </div>
                <p className="text-sm line-height-3">
                  {localeText("extrapayFlowHowYouReceiveBody")}
                </p>
                <p className="text-sm line-height-3">
                  {localeText("extrapayFlowHowYouReceiveDebit")}
                </p>
                <p className="text-sm line-height-3 mb-0">
                  {localeText("extrapayFlowHowYouReceivePartial")}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <ExtrapayInfoNote text={localeText("extrapayFlowDebitNotice")} />
          </div>
        </>
      )}

      <WithdrawConfirmDialog
        visible={confirmVisible}
        onHide={() => setConfirmVisible(false)}
        address={address.trim()}
        amount={parsedAmount}
        balanceAfter={Math.max(
          overview.excBalance - (Number.isFinite(parsedAmount) ? parsedAmount : 0),
          0,
        )}
        checked={checked}
        onCheck={setChecked}
        onConfirm={requestCode}
        submitting={sendingCode}
      />
      <SecurityValidationModal
        visible={otpVisible}
        onHide={() => setOtpVisible(false)}
        email={userInfo?.email}
        amountLabel={localeText("extrapayFlowExcAmount", {
          amount: formatPrice(Number.isFinite(parsedAmount) ? parsedAmount : 0),
        })}
        recipientName={address.trim()}
        confirmTemplateKey="securityValidationConfirmWithdrawal"
        codeLength={EXTRAPAY_WITHDRAW_OTP_LENGTH}
        codeTtlMs={EXTRAPAY_WITHDRAW_OTP_TTL_MS}
        validating={submitting}
        resending={resendingCode}
        onValidate={confirmWithdraw}
        onResend={resendCode}
      />
      <WithdrawSuccessDialog
        visible={Boolean(created)}
        onHide={() => setCreated(null)}
        withdrawal={created}
        balance={balanceAfter}
        onTrack={() =>
          history.push({
            pathname: extrapayWithdrawDetailsPath(created.id),
            state: { withdrawal: created },
          })
        }
        onList={() => history.push(extrapayTabPath("saques"))}
      />
    </DashboardLayout>
  );
};

export default ExtrapayWithdrawPage;
